import React, { useEffect, useRef } from 'react';
import { Box, useTheme } from '@mui/material';
import MessageList from './MessageList.jsx';

/**
 * MessageHistory component that encapsulates message display container and auto-scrolling logic.
 * This component eliminates duplicate scrolling logic across Chat, Simplechat, and Demo components.
 *
 * @param {Array} messages - Array of message objects to display
 * @param {Object} containerSx - Optional custom styling for the container Box
 * @param {string} containerId - Optional ID for the container element
 * @param {boolean} showMessageActions - Whether to show message actions (copy, feedback, etc.)
 * @param {React.Ref} chatEndRef - Optional ref to attach to the bottom marker element
 */
const MessageHistory = ({
  messages,
  containerSx = {},
  containerId = "chat",
  isProcessing = false,
  showMessageActions = true,
  chatEndRef = null
}) => {
  const internalChatEnd = useRef(null);
  const chatEnd = chatEndRef || internalChatEnd;
  const theme = useTheme();

  // Auto-scroll to bottom when messages update
  useEffect(() => {
    if (chatEnd.current) {
      chatEnd.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, chatEnd]);

  // Default container styling with ability to override
  const defaultContainerSx = {
    flex: 1,
    overflowY: 'auto',
    overflowX: 'hidden', // Prevent horizontal scrollbar
    maxWidth: '100%', // Ensure it doesn't overflow its container
    ...containerSx
  };

  return (
    <Box 
      sx={{
        ...defaultContainerSx,
        // Theme-aware styles for chat content (replaces MessageHistory.css)
        '& #chat ul ul, & #chat ul ol': {
          // TODO: Complete this rule if needed for nested lists
        },
        '& #chat li': {
          display: 'inline',
          lineHeight: '1.41em'
        },
        '& #chat h1, & #chat h2, & #chat h3, & #chat h4': {
          display: 'inline'
        },
        '& #chat h1': {
          fontSize: '2em',
          lineHeight: '1em'
        },
        '& #chat pre': {
          borderLeft: `2px solid ${theme.palette.divider}`,
          paddingLeft: '2rem'
        }
      }}
      id={containerId}
    >
      <MessageList messages={messages} chatEnd={chatEnd} isProcessing={isProcessing} showMessageActions={showMessageActions} />
    </Box>
  );
};

export default React.memo(MessageHistory);
